import '@/assets/styles/quill.snow.css';

import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import weekday from 'dayjs/plugin/weekday';
import { ErrorInfo, FC, ReactNode, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import 'react-phone-number-input/style.css';
import { RouterProvider } from 'react-router';
import { ToastContainer } from 'react-toastify';

import { VersionChecker } from '@/components/versionChecker/VersionChecker.comp.tsx';
import { QueryClientWrapper } from '@/config/QueryClientWrapper.comp.tsx';
import { knowMeTheme } from '@/config/theme/theme.ts';
import { useMaintenanceState } from '@/contexts/maintenance/Maintenance.context.ts';
import { captureAppException } from '@/monitoring/sentry.util.ts';
import { router } from '@/router.tsx';
import { MaintenanceView } from '@/views/util/maintenance/Maintenance.view.tsx';

dayjs.extend(weekday);
dayjs.locale('pl');

const MaintenanceGate: FC<{ children: ReactNode }> = ({ children }) => {
  const { active } = useMaintenanceState();
  if (active) return <MaintenanceView />;
  return <>{children}</>;
};

function App() {
  const handleError = (error: unknown, info: ErrorInfo) => {
    if (
      import.meta.env.VITE_ENVIRONMENT !== 'local' &&
      error instanceof Error &&
      error.message.includes('dynamically imported module')
    ) {
      globalThis.location.reload();
      return;
    }

    captureAppException(error, {
      componentStack: info.componentStack ?? undefined,
      contextName: 'react-error-boundary',
    });

    console.debug('ErrorBoundary', error, info);
  };

  useEffect(() => {
    const resetDone = localStorage.getItem('RESET_DONE');
    if (resetDone) {
      return;
    }
    localStorage.clear();
    localStorage.setItem('RESET_DONE', 'true');
  }, []);

  return (
    <LocalizationProvider adapterLocale="pl" dateAdapter={AdapterDayjs}>
      <ThemeProvider theme={knowMeTheme}>
        <CssBaseline />
        <ToastContainer draggable={false} limit={4} newestOnTop={false} position="top-center" />
        <ErrorBoundary fallback={<div>Something went wrong</div>} onError={handleError}>
          <MaintenanceGate>
            <QueryClientWrapper>
              <VersionChecker />
              <RouterProvider router={router} />
            </QueryClientWrapper>
          </MaintenanceGate>
        </ErrorBoundary>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
